import * as React from 'react'
import { unstable_batchedUpdates } from 'react-dom'
import { Data } from 'rete/types/core/data'
import { DBModels } from '../state/db_models'
import { LayerPalette, buildLayerPaletteSections, layerCatalogKey } from './layer_palette'
import { DatasetCache, MapView, ModelOutputCache } from '../map/map_view'
import { ModelView, Transform } from './model_view'
import { AegisSessionProvider } from '../aegis/session'
import type { AgentActions } from '../aegis/types'
import './project_editor.css'
import { reduce } from '../state/reducer'
import { CollapsedSidebar, Sidebar } from './sidebar'
import { PublicSidebar } from './public_sidebar'
import { DatasetLayer, defaultProject, Layer, ModelOutputLayer, Project } from '../state'
import { Toolbar } from './toolbar'
import { PublicToolbar } from './public_toolbar'
import type { PublicExtentSwitchApi } from '../aegis/types'
import { gridGeographyKey, projectGeographyKey } from '../modelling/components/base_component'
import { extentDisplayName, extentMatchesProject, isPublicExtentChoice, projectWithPublicExtent, PublicExtentChoice, usePublicShareSettings } from './public_share_settings'
import {
  setPublicLayerActivator,
  setPublicMapOutputLinks,
  useMapOutputHighlightSnapshot,
} from './map_output_highlight'
import { debounce, get } from 'lodash'
import { getExpressions, getDataset, getDatasets, getAnnotations, getTeamData, saveModelOutput, type AnnotationRecord, type CompiledDatasetRecord, type TeamDataRecord } from '../state/saved_dataset'
import { TileGridJSON, BooleanTileGrid, NumericTileGrid, CategoricalTileGrid } from '../modelling/tile_grid'
import { resolvePaletteForCategoricalLayer } from '../layers/categorical_layer_colors'
import { AnalysisPanel } from '../snapshot/analysis_panel'
import { Extent } from 'ol/extent'
import { getUrlParam, getUrlParamAsBoolean, getUrlParamAsNumber, getUrlParamAsArray, getAllUrlParams } from '../utils/url_params'
import { calculateProjectExtent, calculateProjectZoom, calculateProjectCenter, extractProjectMaskSettings } from '../utils/project_utils'
import { isRegionAvailable } from "../utils/region_permissions"
import { reconcileMapStackWithProject, projectOrderFromStack, isSameLayerIdMultiset, type MapStackSlot } from '../map/map_layer_stack'
import type { LocalGeotiffLayerState } from '../map/local_geotiff_preview'

function nextProjectLayerId(layers: Record<number, Layer>): number {
  const ids = Object.keys(layers).map(key => parseInt(key, 10)).filter(id => !Number.isNaN(id))
  return Math.max(0, ...ids) + 1
}

export enum Tab {
  MapView,
  ModelView,
}

export interface ProjectPermissions {
  DefraHedgerows: boolean
  KewRgb25cm: boolean
  KewSamples: boolean
  NATMAPSoil: boolean
  KewLidar: boolean
  PlanetData: boolean
  isUserGuest: boolean
  ukEnabled: boolean
  indonesiaEnabled: boolean
}

export interface TeamExtentData {
  name: string
  value: Extent
  id: number
  team_id: number
  externalLink?: string
  layer?: string
  cql?: string
  region?: string
  level?: number | null
  parent_id?: number | null
}

export function exportLayerAsGeoTiff(layer: ModelOutputLayer | DatasetLayer): Promise<void> {
  return Promise.resolve()
}

function exportColorsForLayer(
  tileGrid: BooleanTileGrid | NumericTileGrid | CategoricalTileGrid,
  layer: ModelOutputLayer | DatasetLayer
) {
  if (tileGrid instanceof CategoricalTileGrid) {
    return resolvePaletteForCategoricalLayer(tileGrid, layer)
  }
  return layer.colors
}

function generateQGISColorMap(tileGridJSON: TileGridJSON, colors: number[][]): string {
  // Generate a .clr file content for QGIS
  // Format: value red green blue alpha
  const lines: string[] = []
  
  if (tileGridJSON.labels) {
    // Sort the labels by their numeric keys
    const sortedLabels = Object.entries(tileGridJSON.labels)
      .map(([key, value]) => [parseInt(key), value])
      .sort(([a], [b]) => a - b)
    
    sortedLabels.forEach(([value, label]) => {
      const colorIndex = value - 1 // Colors array is 0-indexed, values are 1-indexed
      const color = colors[colorIndex] || [128, 128, 128, 255] // Default gray color
      
      // Colors are already in 0-255 range
      const r = Math.round(color[0])
      const g = Math.round(color[1])
      const b = Math.round(color[2])
      const a = Math.round(color[3] * 255 || 255)
      
      // Add label name for better readability
      lines.push(`${value} ${r} ${g} ${b} ${a} ${label}`)
    })
  }
  
  return lines.join('\n')
}

/**
 * Check if debug mode is enabled via URL parameter
 * @deprecated Use getUrlParamAsBoolean('debug') from '../utils/url_params' instead
 */
export function isDebugModeEnabled(): boolean {
  return getUrlParamAsBoolean('debug');
}

export interface TeamComponentData {
  id: number
  name: string
  component_type: string
  settings: Record<string, unknown>
}

export interface ProjectEditorProps {
  projectId: number
  projectSource: Project
  backButtonPath: string
  dbModels: DBModels
  teamId: number
  teamName: string
  teamComponents: TeamComponentData[]
  permissions: ProjectPermissions
  teamExtents: TeamExtentData[]
  /** Whether the team has a Groq API key configured (enables the AEGIS agent). */
  groqConfigured?: boolean
  /** Whether the team's selected AEGIS provider has an API key configured. */
  agentConfigured?: boolean
  /** Human-readable label for the team's effective AEGIS provider. */
  agentProvider?: string
  /** Human-readable label for the team's effective AEGIS model. */
  agentModel?: string
  /** Raw provider id ("groq" | "anthropic" | "deepseek") used to route the agent API. */
  agentProviderId?: string
  /** Raw model id sent to the provider API (e.g. "claude-sonnet-4-6"). */
  agentModelId?: string
}

export interface SnapshotExtentData {
  value: Extent,
  shape?: any,
  layer?: string,
  cql?: string,
  region?: string,
  externalLink?: string
}

// customisation point, used to set the zoom level (resolution of the tilegrid). higher values can lead to performance issues.
export const MAX_TILES = 10000000

const LANDSCAPES_V2_API = "https://landscapes.wearepal.ai/api/v2"
/** geotiff_to_shp smoothing; 0 for now — adjust if polygon output needs tuning. */
const GEOTIFF_TO_SHP_SMOOTH = 0
const GEOTIFF_TO_SHP_SMOOTH_ITERS = 0
const PAINTBRUSH_BROADCAST_CHANNEL = "landscapes:paintbrush-updates"

export function ProjectEditor({ projectId, projectSource, backButtonPath, dbModels, teamId, teamName, teamComponents, permissions, teamExtents, groqConfigured, agentConfigured, agentProvider, agentModel, agentProviderId, agentModelId }: ProjectEditorProps) {
  const [availableComponents, setAvailableComponents] = React.useState<{ name: string, label: string, category: string }[]>([])
  const [paletteSources, setPaletteSources] = React.useState<{
    teamDatasets: CompiledDatasetRecord[]
    teamDataLayers: TeamDataRecord[]
    paintbrushAnnotations: AnnotationRecord[]
  }>({
    teamDatasets: [],
    teamDataLayers: [],
    paintbrushAnnotations: [],
  })
  const [state, dispatch] = React.useReducer(reduce, {
    project: { ...defaultProject, ...projectSource },
    hasUnsavedChanges: false,
    autoProcessing: false
  })
  // Keep a live ref so agent style tools can read layers after an
  // add-then-style batch without waiting for a re-render of closed-over state.
  const projectLayersRef = React.useRef(state.project.layers)
  projectLayersRef.current = state.project.layers
  const reservedLayerIdRef = React.useRef(0)
  reservedLayerIdRef.current = nextProjectLayerId(state.project.layers) - 1
  const allocateProjectLayerId = () => {
    const id = Math.max(nextProjectLayerId(projectLayersRef.current), reservedLayerIdRef.current + 1)
    reservedLayerIdRef.current = id
    return id
  }

  React.useEffect(() => {
    let cancelled = false
    Promise.all([
      getDatasets(teamId),
      getTeamData(teamId),
      getAnnotations(teamId),
    ]).then(([datasets, teamData, annotations]) => {
      if (cancelled) return
      setPaletteSources({
        teamDatasets: datasets,
        teamDataLayers: teamData.filter(datum => datum.publish_status === 'published' && !!datum.geoserver_layer_id),
        paintbrushAnnotations: annotations.filter(annotation => !!annotation.geojson_url),
      })
    }).catch(error => {
      console.error('Error fetching map layers for public share:', error)
    })
    return () => { cancelled = true }
  }, [teamId])

  const availableMapLayers = React.useMemo(() => {
    const activeKeys = new Set(
      Object.values(state.project.layers)
        .filter((layer): layer is Layer => !!layer && !(layer.type === "DatasetLayer" && layer.deleted))
        .map(layerCatalogKey)
    )
    return buildLayerPaletteSections({
      ...paletteSources,
      teamName,
      permissions,
      dbModels,
    })
      .filter(section => isRegionAvailable(section.region, permissions))
      .flatMap(section => section.items.map(item => ({
        id: `layer-${item.key}`,
        label: item.layer.name,
        category: section.title,
        active: activeKeys.has(layerCatalogKey(item.layer)),
      })))
  }, [paletteSources, teamName, permissions, dbModels, state.project.layers])

  // Read URL parameters
  const debug = getUrlParamAsBoolean('debug');
  const urlParams = getAllUrlParams();

  // Test script for getting the extent of a new layer

  // getGeoJSONExtentFromWFS("https://landscapes.wearepal.ai/geoserver/wfs?", {"typeNames": "ind:watershed_bolango"}, "1=1").then((ext) => {
  //   console.log(`${ext.minX},${ext.minY},${ext.maxX},${ext.maxY}`)
  // })

  const [sidebarVisible, setSidebarVisible] = React.useState(true)
  const [layerPaletteVisible, setLayerPaletteVisible] = React.useState(false)
  const [currentTab, setCurrentTab] = React.useState(Tab.MapView)

  const [showAP, setShowAP] = React.useState(false)
  const [selectedArea, setSelectedArea] = React.useState<Extent | null>(null)
  const [extentShape, setExtentShape] = React.useState<SnapshotExtentData | undefined>(undefined)
  const [selectedLayer, setSelectedLayer] = React.useState<Layer | null>(null)
  const [mapLayerStack, setMapLayerStack] = React.useState<MapStackSlot[]>([])
  const [localGeotiffLayers, setLocalGeotiffLayers] = React.useState<Record<string, LocalGeotiffLayerState>>({})
  const [selectedLocalGeotiffId, setSelectedLocalGeotiffId] = React.useState<string | null>(null)
  const [highlightedCategoryGroupKey, setHighlightedCategoryGroupKey] = React.useState<string | null>(null)

  const projectAllLayersOrderKey = state.project.allLayers.join(',')
  /** Same render as project state — avoids a frame where the stack still references deleted layer ids (useEffect runs too late). */
  const mapLayerStackReconciled = React.useMemo(
    () => reconcileMapStackWithProject(mapLayerStack, state.project.allLayers),
    [mapLayerStack, projectAllLayersOrderKey],
  )
  React.useEffect(() => {
    setMapLayerStack(prev => {
      if (prev.length === 0 && state.project.allLayers.length > 0) {
        return state.project.allLayers.map(layerId => ({ kind: 'project' as const, layerId }))
      }
      return reconcileMapStackWithProject(prev, state.project.allLayers)
    })
  }, [projectAllLayersOrderKey])

  const onLocalGeotiffParsed = React.useCallback((items: LocalGeotiffLayerState[]) => {
    setLocalGeotiffLayers(prev => {
      const next = { ...prev }
      for (const it of items) {
        next[it.id] = it
      }
      return next
    })
    setMapLayerStack(prev => [...prev, ...items.map(it => ({ kind: 'localGeotiff' as const, localId: it.id }))])
  }, [])

  const mutateLocalGeotiff = React.useCallback((id: string, data: Partial<Pick<LocalGeotiffLayerState, 'name' | 'opacity' | 'visible'>>) => {
    setLocalGeotiffLayers(prev => {
      const cur = prev[id]
      if (!cur) {
        return prev
      }
      return { ...prev, [id]: { ...cur, ...data } }
    })
  }, [])

  const removeLocalGeotiff = React.useCallback((id: string) => {
    setLocalGeotiffLayers(prev => {
      const { [id]: _removed, ...rest } = prev
      return rest
    })
    setMapLayerStack(prev => prev.filter(s => !(s.kind === 'localGeotiff' && s.localId === id)))
    setSelectedLocalGeotiffId(cur => (cur === id ? null : cur))
  }, [])

  const reorderMapLayers = React.useCallback((next: MapStackSlot[]) => {
    const order = projectOrderFromStack(next)
    if (!isSameLayerIdMultiset(state.project.allLayers, order)) {
      return
    }
    setMapLayerStack(next)
    dispatch({ type: 'SetLayerOrder', order })
  }, [dispatch, state.project.allLayers])

  const publicShare = usePublicShareSettings()
  const publicExtents = React.useMemo(
    () => (publicShare?.extents ?? []).filter(isPublicExtentChoice),
    [publicShare]
  )
  const matchedExtentId = React.useMemo(() => {
    const fromPublic = publicExtents.find(extent => extentMatchesProject(extent, projectSource))
    if (fromPublic) return fromPublic.id
    const fromTeam = teamExtents.find(extent => extentMatchesProject(extent, projectSource))
    return fromTeam?.id ?? null
  }, [publicExtents, teamExtents, projectSource])
  const [extentOverride, setExtentOverride] = React.useState<PublicExtentChoice | null>(null)
  const geographySource = React.useMemo(
    () => extentOverride ? projectWithPublicExtent(projectSource, extentOverride) : projectSource,
    [projectSource, extentOverride]
  )
  const publicExtentChoices = React.useMemo(() => {
    if (publicExtents.length === 0) return []
    const included = new Map<number, { id: number, name: string, level: number | null }>()
    const add = (id: number, name: string, level?: number | null) => {
      if (!included.has(id)) included.set(id, { id, name, level: level ?? null })
    }
    if (matchedExtentId != null) {
      const known = publicExtents.find(extent => extent.id === matchedExtentId)
        ?? teamExtents.find(extent => extent.id === matchedExtentId)
      if (known) add(known.id, known.name, known.level)
    }
    publicExtents.forEach(extent => add(extent.id, extent.name, extent.level))
    const order = new Map(teamExtents.map((extent, index) => [extent.id, index]))
    return Array.from(included.values()).sort((a, b) => {
      const left = order.get(a.id) ?? Number.MAX_SAFE_INTEGER
      const right = order.get(b.id) ?? Number.MAX_SAFE_INTEGER
      return left - right || a.name.localeCompare(b.name)
    })
  }, [publicExtents, teamExtents, matchedExtentId])
  const includeProjectExtentOption = matchedExtentId == null && publicExtentChoices.length > 0
  const extentChoiceCount = publicExtentChoices.length + (includeProjectExtentOption ? 1 : 0)
  const showExtentSwitch = !publicShare?.lockExtent && extentChoiceCount >= 2
  const activeExtentLabel = React.useMemo(() => {
    if (!publicShare?.lockExtent) return null
    if (extentOverride) return extentDisplayName(extentOverride.name)
    if (matchedExtentId != null) {
      const known = publicExtents.find(extent => extent.id === matchedExtentId)
        ?? teamExtents.find(extent => extent.id === matchedExtentId)
      if (known) return extentDisplayName(known.name)
    }
    return includeProjectExtentOption ? "Project area" : null
  }, [publicShare?.lockExtent, extentOverride, matchedExtentId, publicExtents, teamExtents, includeProjectExtentOption])
  const aegisCanSwitchExtent = extentChoiceCount >= 2

  // Retrieve extent and zoom from the active area. Memoized so consumers
  // (e.g. MapView effects) get a stable reference across unrelated parent
  // re-renders — otherwise overlays would be torn down on every render,
  // hiding the map-click probe popup as soon as it appears.
  const projectExtent = React.useMemo(
    () => calculateProjectExtent(geographySource),
    [geographySource]
  )
  const projectZoom = calculateProjectZoom(projectExtent)
  const { mask: projectMask, maskSource: projectMaskSource, maskCQL: projectMaskCQL } = extractProjectMaskSettings(geographySource)
  const publishedGeographyRef = React.useRef("")
  publishedGeographyRef.current = projectGeographyKey({
    extent: projectExtent,
    zoom: projectZoom,
    mask: projectMask,
    maskLayer: projectMaskSource,
    maskCQL: projectMaskCQL,
    maskShape: geographySource.shape,
    externalLink: geographySource.externalLink,
  })
  const projectExtentShape = React.useMemo<SnapshotExtentData>(() => ({
    value: projectExtent,
    shape: geographySource.shape,
    layer: geographySource.layer,
    cql: geographySource.cql,
    externalLink: geographySource.externalLink,
  }), [projectExtent, geographySource.shape, geographySource.layer, geographySource.cql, geographySource.externalLink])

  const selectPublicExtent = React.useCallback((id: number | null) => {
    if (id == null || (matchedExtentId != null && id === matchedExtentId)) {
      setExtentOverride(null)
      return
    }
    setExtentOverride(publicExtents.find(extent => extent.id === id) ?? null)
  }, [matchedExtentId, publicExtents])
  const publicExtentApiRef = React.useRef<PublicExtentSwitchApi | null>(null)
  const currentPublicExtentId = extentOverride?.id ?? (includeProjectExtentOption ? null : matchedExtentId)
  const publicExtentAreas = !publicShare || !aegisCanSwitchExtent
    ? []
    : [
      ...(includeProjectExtentOption ? [{ id: null as number | null, name: "Project area", current: currentPublicExtentId == null }] : []),
      ...publicExtentChoices.map(extent => ({
        id: extent.id as number | null,
        name: extentDisplayName(extent.name),
        current: extent.id === currentPublicExtentId,
      })),
    ]
  publicExtentApiRef.current = publicShare ? {
    list: () => publicExtentAreas,
    switchTo: (id) => {
      const match = publicExtentAreas.find(area => area.id === id)
      if (!match) return { ok: false, error: "That area is not available on this public project." }
      if (match.current) return { ok: true, id: match.id, name: match.name, unchanged: true }
      selectPublicExtent(id)
      return { ok: true, id: match.id, name: match.name }
    },
  } : null

  const focusAddedPublicLayers = React.useCallback((keepLayerIds: number[]) => {
    const keep = new Set(keepLayerIds)
    const selectedId = keepLayerIds[keepLayerIds.length - 1]
    Object.entries(projectLayersRef.current).forEach(([key, layer]) => {
      const id = Number(key)
      if (keep.has(id) || layer.type === "OsmLayer" || layer.visible === false) return
      dispatch({ type: "MutateLayer", id, data: { visible: false } })
    })
    setLocalGeotiffLayers(prev => {
      let changed = false
      const next: typeof prev = { ...prev }
      Object.keys(next).forEach(id => {
        if (next[id].visible === false) return
        next[id] = { ...next[id], visible: false }
        changed = true
      })
      return changed ? next : prev
    })
    setSelectedLocalGeotiffId(null)
    if (selectedId !== undefined) dispatch({ type: "SelectLayer", id: selectedId })
  }, [dispatch])
  const pendingPublicLayerIdsRef = React.useRef<number[]>([])
  const publicFocusWaitsForProcessRef = React.useRef(false)
  const isProcessingRef = React.useRef(false)
  const noteAddedPublicLayer = React.useCallback((id: number) => {
    pendingPublicLayerIdsRef.current = pendingPublicLayerIdsRef.current.concat(id)
    if (isProcessingRef.current) publicFocusWaitsForProcessRef.current = true
  }, [])
  const highlightedOutputNodeId = useMapOutputHighlightSnapshot().highlightedNodeId
  const highlightedOutputLayerId = React.useMemo(() => {
    if (!publicShare || highlightedOutputNodeId == null) return undefined
    const match = Object.entries(state.project.layers).find(([, layer]) =>
      layer?.type === "ModelOutputLayer" && layer.nodeId === highlightedOutputNodeId
    )
    return match ? Number(match[0]) : undefined
  }, [publicShare, highlightedOutputNodeId, state.project.layers])

  /**
   * Map render order: hoist the currently selected layer to the top of the draw
   * order without changing the sidebar's stored order. A chat hover on a
   * public map output hoists that layer instead, for as long as the pointer
   * is on the link. Stack is bottom→top, so the chosen slot moves to the end.
   */
  const effectiveMapLayerStack = React.useMemo(() => {
    const isHoisted = (slot: MapStackSlot) => {
      if (highlightedOutputLayerId !== undefined) {
        return slot.kind === "project" && slot.layerId === highlightedOutputLayerId
      }
      if (selectedLocalGeotiffId !== null) {
        return slot.kind === "localGeotiff" && slot.localId === selectedLocalGeotiffId
      }
      if (state.selectedLayer !== undefined) {
        return slot.kind === "project" && slot.layerId === state.selectedLayer
      }
      return false
    }
    const idx = mapLayerStackReconciled.findIndex(isHoisted)
    if (idx === -1 || idx === mapLayerStackReconciled.length - 1) {
      return mapLayerStackReconciled
    }
    const next = mapLayerStackReconciled.slice()
    const [chosen] = next.splice(idx, 1)
    next.push(chosen)
    return next
  }, [mapLayerStackReconciled, state.selectedLayer, selectedLocalGeotiffId, highlightedOutputLayerId])

  // Set the initial map view zoom and center to the project extent.
  const [mapViewZoom, setMapViewZoom] = React.useState(9)
  const [mapViewCenter, setMapViewCenter] = React.useState<[number, number]>(calculateProjectCenter(projectExtent))
  const [showExtent, setShowExtent] = React.useState(false)


  const [modelViewTransform, setModelViewTransform] = React.useState<Transform>({ x: 0, y: 0, k: 1 })
  const [modelEditorApi, setModelEditorApi] = React.useState<import('./model_editor_api').ModelEditorApi | null>(null)
  const agentActionsRef = React.useRef<AgentActions | undefined>(undefined)
  const isMapViewRef = React.useRef(currentTab === Tab.MapView)
  isMapViewRef.current = currentTab === Tab.MapView
  const modelRef = React.useRef(state.project.model)
  modelRef.current = state.project.model
  const onAgentActionsReady = React.useCallback((actions: AgentActions | undefined) => {
    agentActionsRef.current = actions
  }, [])
  const [modelOutputCache, setModelOutputCache] = React.useState<ModelOutputCache>({})
  const [datasetOutputCache, setDatasetOutputCache] = React.useState<DatasetCache>({})

  const [isProcessing, setProcessing] = React.useState(false)
  isProcessingRef.current = isProcessing
  React.useEffect(() => {
    if (!publicShare) return
    if (isProcessing) {
      if (pendingPublicLayerIdsRef.current.length > 0) publicFocusWaitsForProcessRef.current = true
      return
    }
    if (!publicFocusWaitsForProcessRef.current) return
    const ids = pendingPublicLayerIdsRef.current
    pendingPublicLayerIdsRef.current = []
    publicFocusWaitsForProcessRef.current = false
    if (ids.length === 0) return
    focusAddedPublicLayers(ids)
  }, [isProcessing, publicShare, focusAddedPublicLayers])
  const [isLoading, setLoading] = React.useState(false)
  const [loadingQueue, setLoadingQueue] = React.useState<number[]>([])
  const [process, setProcess] = React.useState(false)
  const activeExtentKey = extentOverride ? `extent-${extentOverride.id}` : "project"
  const skipExtentReprocessRef = React.useRef(true)
  React.useEffect(() => {
    if (!publicShare) return
    if (skipExtentReprocessRef.current) {
      skipExtentReprocessRef.current = false
      return
    }
    setModelOutputCache({})
    setProcessing(true)
    const timer = window.setTimeout(() => setProcess(true), 750)
    return () => window.clearTimeout(timer)
  }, [activeExtentKey, publicShare])

  /**
   * Staggered load order: fully hydrate the Rete graph in `ModelView`
   * before kicking off saved-dataset fetches. Loading both at once on a
   * heavy project saturates the main thread (Rete's `fromJSON`
   * connection replay competes with the dataset GET decode/render path),
   * which produces a visible stall for the user. Flipped to true exactly
   * once via `ModelView.onInitialLoadComplete`. Tracked as both state and
   * ref — state drives the replay effect, ref lets the deferred-loader
   * callback (captured by MapView) make a synchronous decision without
   * stale-closure issues.
   */
  const [modelLoaded, setModelLoaded] = React.useState(false)
  const modelLoadedRef = React.useRef(false)
  const pendingDatasetLoadsRef = React.useRef<DatasetLayer[]>([])

  const [downloading, setDownloading] = React.useState<string | null>(null)
  const [exportStatus, setExportStatus] = React.useState<{
    status: 'idle' | 'preparing' | 'uploading' | 'processing' | 'downloading' | 'completed' | 'error'
    progress: number
    message: string
    error?: string
  }>({
    status: 'idle',
    progress: 0,
    message: ''
  })

  const [extraInfo, setExtraInfo] = React.useState<string | null>(null)
  const [paintbrushUpdateMessage, setPaintbrushUpdateMessage] = React.useState<string | null>(null)

  React.useEffect(() => {
    if (typeof BroadcastChannel === "undefined") return
    const channel = new BroadcastChannel(PAINTBRUSH_BROADCAST_CHANNEL)
    let clearNoticeTimer: number | null = null

    const updateVersionedUrl = (url: string, version: number) => {
      try {
        const parsed = new URL(url, window.location.origin)
        parsed.searchParams.set("v", String(version))
        return parsed.toString()
      } catch {
        const separator = url.includes("?") ? "&" : "?"
        return `${url}${separator}v=${version}`
      }
    }

    const onMessage = (event: MessageEvent) => {
      const payload = event.data as { type?: string; annotationId?: number; teamId?: number; updatedAt?: number } | null
      if (!payload || payload.type !== "annotation-updated") return
      if (payload.teamId !== teamId) return
      if (payload.annotationId == null) return

      const updatedAt = payload.updatedAt ?? Date.now()
      const matchingLayerIds = state.project.allLayers.filter(layerId => {
        const layer = state.project.layers[layerId]
        return layer?.type === "PaintbrushGeoJSONLayer" && layer.annotationId === payload.annotationId
      })

      if (matchingLayerIds.length === 0) return

      matchingLayerIds.forEach(layerId => {
        const layer = state.project.layers[layerId]
        if (layer?.type !== "PaintbrushGeoJSONLayer") return
        dispatch({
          type: "MutateLayer",
          id: layerId,
          data: { geojsonUrl: updateVersionedUrl(layer.geojsonUrl, updatedAt) }
        })
      })

      setPaintbrushUpdateMessage("Updating Paintbrush layer…")
      if (clearNoticeTimer) window.clearTimeout(clearNoticeTimer)
      clearNoticeTimer = window.setTimeout(() => setPaintbrushUpdateMessage(null), 1800)
    }

    channel.addEventListener("message", onMessage)
    return () => {
      channel.removeEventListener("message", onMessage)
      channel.close()
      if (clearNoticeTimer) window.clearTimeout(clearNoticeTimer)
    }
  }, [teamId, state.project.allLayers, state.project.layers])

  const loadDataset = debounce((layer: DatasetLayer)=>{      
    if(!layer.deleted) {              
      getDatasets(teamId).then((datasets) => {
        // check if deleted
        if (!datasets.some((d) => d.id === layer.id)) {
          layer.deleted = true
        }
      })
    }

    if (isLoading) return // prevent spamming the server
    if (layer.deleted) return // prevent loading deleted layers

    setLoading(true)
    getDataset(layer.id, teamId, (err, out, colorMetadata) => {
      // Collapse every state change for this completion into a single
      // React commit. Without this, each setState in a Promise `.then()`
      // (React 17 doesn't auto-batch outside React-managed events)
      // triggers its own render, so `MapView`'s layer-setup effect
      // re-runs on the render that produced the new `datasetCache`
      // reference — *before* `setLoading(false)` has been applied. The
      // `loadDataset` closure handed back to `MapView` on that intermediate
      // render therefore captures `isLoading: true` and the next
      // debounced load exits via the early-return at the top of this
      // function, stranding the queue until the user manually re-renders
      // (e.g. by switching tabs). Batching guarantees `isLoading: false`
      // is live for the single render where `datasetCache` flips.
      unstable_batchedUpdates(() => {
        if (out && !err) {
          // IMPORTANT: write through `setDatasetOutputCache` (not direct
          // mutation) so the cache *reference* changes — that's what
          // makes React re-run `MapView`'s layer-setup effect (it only
          // re-runs when its `datasetCache` dep referentially differs).
          setDatasetOutputCache(prev => ({ ...prev, [layer.id]: out }))

          if (colorMetadata && (colorMetadata.colors || colorMetadata.fill)) {
            const layerId = Array.from(state.project.allLayers).find(id => {
              const l = state.project.layers[id]
              return l.type === "DatasetLayer" && l.id === layer.id
            })

            if (layerId !== undefined) {
              const updates: Partial<DatasetLayer> = {}
              if (colorMetadata.colors) {
                updates.colors = colorMetadata.colors
              }
              if (colorMetadata.fill) {
                updates.fill = colorMetadata.fill
              }
              dispatch({ type: "MutateLayer", id: layerId, data: updates })
            }
          }
        }
        // Use a functional update — the closed-over `loadingQueue` is the
        // snapshot from whichever render created this debounced
        // `loadDataset`, and other layers may have been pushed onto the
        // live queue in the meantime.
        setLoadingQueue(prev => prev.filter(id => id !== layer.id))
        setLoading(false)
      })
    })

  }, 750)

  /**
   * Public entry point used by `MapView`. Defers the actual fetch until
   * the model graph has finished loading; while the gate is closed,
   * layers are queued and replayed by the `modelLoaded` effect below.
   * The next reactive pass through `MapView`'s layer-setup effect (driven
   * by `datasetCache` updates) will pick up any layers that the
   * debounced replay coalesces past, so we only need to seed the chain.
   */
  const requestDatasetLoad = React.useCallback((layer: DatasetLayer) => {
    if (!modelLoadedRef.current) {
      if (!pendingDatasetLoadsRef.current.some(l => l.id === layer.id)) {
        pendingDatasetLoadsRef.current.push(layer)
      }
      return
    }
    loadDataset(layer)
  }, [loadDataset])

  React.useEffect(() => {
    modelLoadedRef.current = modelLoaded
    if (!modelLoaded) return
    const pending = pendingDatasetLoadsRef.current
    pendingDatasetLoadsRef.current = []
    pending.forEach(layer => loadDataset(layer))
  }, [modelLoaded])

  const saveProject = async () => {
    const method = 'PATCH'

    const headers = new Headers()
    headers.set('X-CSRF-Token', (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement).content)

    const body = new FormData()
    body.set('project[source]', JSON.stringify(state.project))

    const response = await fetch(`/projects/${projectId}`, { method, headers, body })

    if (response.ok) {
      const data = await response.json().catch(() => ({}))
      dispatch({
        type: "FinishSave",
        name: typeof data.name === "string" ? data.name : undefined,
      })
    }
    else {
      throw new Error(response.statusText)
    }
  }

  React.useEffect(() => {
    if (!publicShare) {
      setPublicMapOutputLinks(null)
      return
    }
    const ids = new Set<number>()
    const nodes = state.project.model?.nodes
    if (nodes) {
      for (const node of Object.values(nodes)) {
        if (node.name === "Map layer") ids.add(node.id)
      }
    }
    setPublicMapOutputLinks(ids)
  }, [publicShare, state.project.model])
  React.useEffect(() => {
    if (!publicShare) {
      setPublicLayerActivator(null)
      return
    }
    setPublicLayerActivator(layerId => {
      dispatch({ type: "SelectLayer", id: layerId })
    })
    return () => setPublicLayerActivator(null)
  }, [publicShare, dispatch])
  const focusMapOutput = React.useCallback((nodeId: number) => {
    setSidebarVisible(true)
    setCurrentTab(Tab.MapView)
    const match = Object.entries(state.project.layers).find(([, layer]) =>
      layer?.type === "ModelOutputLayer" && layer.nodeId === nodeId
    )
    if (!match) return
    dispatch({ type: "SelectLayer", id: Number(match[0]) })
  }, [state.project.layers])
  const autoRunStartedRef = React.useRef(false)
  React.useEffect(() => {
    if (!publicShare?.autoRun || !modelLoaded || autoRunStartedRef.current) return
    autoRunStartedRef.current = true
    setProcessing(true)
    window.setTimeout(() => setProcess(true), 750)
  }, [publicShare?.autoRun, modelLoaded])
  const seededPublicLayerRef = React.useRef(false)
  React.useEffect(() => {
    if (!publicShare || seededPublicLayerRef.current || state.selectedLayer != null) return
    const isLegendLayer = (candidate: typeof mapLayerStackReconciled[number]) => {
      if (candidate.kind !== "project") return false
      const layer = state.project.layers[candidate.layerId]
      if (!layer || layer.visible === false) return false
      if (layer.type === "DatasetLayer" && layer.deleted) return false
      return layer.type === "ModelOutputLayer" || layer.type === "DatasetLayer"
    }
    const slot = mapLayerStackReconciled.find(isLegendLayer) ?? mapLayerStackReconciled.find(candidate => {
      if (candidate.kind !== "project") return false
      const layer = state.project.layers[candidate.layerId]
      return !!layer && layer.visible !== false && !(layer.type === "DatasetLayer" && layer.deleted)
    })
    if (!slot || slot.kind !== "project") return
    seededPublicLayerRef.current = true
    dispatch({ type: "SelectLayer", id: slot.layerId })
  }, [publicShare, state.selectedLayer, mapLayerStackReconciled, state.project.layers])

  return (
    <div style={{ height: publicShare ? "100vh" : "calc(100vh - 3.5rem)" }} className="d-flex flex-column">
      {publicShare ? (
        <PublicToolbar
          projectName={state.project.name}
          isProcessing={isProcessing}
          isLoading={isLoading}
          loadingQueue={loadingQueue}
          projectLayers={state.project.layers}
          manualProcessing={() => {
            setProcessing(true)
            const staggeredProcess = debounce(() => {
              setProcess(true)
            }, 750)
            staggeredProcess()
          }}
          autoRun={!!publicShare.autoRun}
          extraInfo={extraInfo}
          extents={showExtentSwitch ? publicExtentChoices : []}
          selectedExtentId={extentOverride?.id ?? matchedExtentId}
          includeProjectOption={includeProjectExtentOption}
          onExtentChange={selectPublicExtent}
          activeExtentLabel={activeExtentLabel}
        />
      ) : (
      <Toolbar
        backButtonPath={backButtonPath}
        teamId={teamId}
        projectName={state.project.name}
        hasUnsavedChanges={state.hasUnsavedChanges}
        setProjectName={name => dispatch({ type: "SetProjectName", name })}
        saveProject={saveProject}
        currentTab={currentTab}
        setCurrentTab={setCurrentTab}
        isProcessing={isProcessing}
        isLoading={isLoading}
        loadingQueue={loadingQueue}
        datasetOutputCache={datasetOutputCache}
        projectLayers={state.project.layers}
        autoProcessing={state.autoProcessing}
        setAutoProcessing={autoprocessing => dispatch({ type: "SetAutoprocessing", autoprocessing })}
        manualProcessing={() => {
          setProcessing(true)
          const staggeredProcess = debounce(() => {
            setProcess(true)
          }, 750)
          staggeredProcess()
        }}
        setShowAP={() => setShowAP(!showAP)}
        showAP={showAP}
        setShowExtent={setShowExtent}
        zoomLevel={projectZoom}
        mapViewZoom={mapViewZoom}
        mapViewCenter={mapViewCenter}
        isUserGuest={permissions.isUserGuest}
        extraInfo={extraInfo}
        projectId={projectId}
        teamExtents={teamExtents}
        currentExtentId={matchedExtentId}
        projectModel={state.project.model}
        availableComponents={availableComponents}
        availableMapLayers={availableMapLayers}
      />
      )}
      <div className={`flex-grow-1 d-flex${publicShare ? " public-stage" : ""}${publicShare && layerPaletteVisible ? " public-stage--palette" : ""}`} style={{ minHeight: 0, position: "relative" }}>
        <AegisSessionProvider
          teamId={teamId}
          projectId={projectId}
          actionsRef={agentActionsRef}
          isMapViewRef={isMapViewRef}
          modelRef={modelRef}
        >
        {/*
          Keep MapView mounted across tab switches and toggle its visibility
          via the `visible` prop (CSS `d-none`). Unmounting it would tear
          down the OpenLayers `Map`, its WebGL tile layers, and every
          decoded `TileGrid` source — switching back then has to rebuild
          all of that and refetch base-map tiles, which is the lag the
          user saw between tabs. The layer palette and analysis panel stay
          tab-conditional because they're cheap to remount. The layers
          sidebar stays mounted (hidden on the model view) so Layers/Detail
          selection is not reset to Layers.
        */}
        <MapView
          visible={currentTab === Tab.MapView}
          highlightedLayerName={
            highlightedOutputLayerId != null
              ? state.project.layers[highlightedOutputLayerId]?.name ?? null
              : null
          }
          projectLayersById={state.project.layers}
          mapLayerStack={effectiveMapLayerStack}
          localGeotiffLayers={localGeotiffLayers}
          onLocalGeotiffParsed={onLocalGeotiffParsed}
          dbModels={dbModels}
          initialZoom={mapViewZoom}
          setZoom={setMapViewZoom}
          initialCenter={mapViewCenter}
          setCenter={setMapViewCenter}
          modelOutputCache={modelOutputCache}
          datasetCache={datasetOutputCache}
          loadTeamDataset={(layer: DatasetLayer) => {
            if(!loadingQueue.includes(layer.id)) loadingQueue.push(layer.id)
            requestDatasetLoad(layer)
            }
          }
          setSelectedArea={setSelectedArea}
          selectedArea={selectedArea}
          selected={publicShare ? false : showAP}
          setSelected={publicShare ? () => undefined : setShowAP}
          enableSnapshot={!publicShare}
          projectExtent={projectExtent}
          showProjectExtent={showExtent}
          extentShape={extentShape}
          setExtentShape={setExtentShape}
          projectExtentShape={projectExtentShape}
          teamId={teamId}
          paintbrushUpdateMessage={paintbrushUpdateMessage}
          onProbeCategoryHighlight={setHighlightedCategoryGroupKey}
          model={state.project.model}
        />
        {currentTab == Tab.MapView && <>
          {
            layerPaletteVisible &&
            <LayerPalette
              hide={() => setLayerPaletteVisible(false)}
              addLayer={layer => {
                if (!publicShare) {
                  dispatch({ type: "AddLayer", layer })
                  return
                }
                const nextId = allocateProjectLayerId()
                dispatch({ type: "AddLayer", layer: { ...layer, visible: true } })
                noteAddedPublicLayer(nextId)
              }}
              dbModels={dbModels}
              getTeamDatasets={() => getDatasets(teamId)}
              getTeamDataLayers={() => getTeamData(teamId)}
              teamId={teamId}
              teamName={teamName}
              permissions={permissions}
            />
          }
          {
            showAP && !publicShare &&
            <AnalysisPanel
              setShowAP={() => setShowAP(false)}
              selectedArea={selectedArea}
              setSelectedArea={setSelectedArea}
              selectedLayer={selectedLayer}
              layerStats={(layer: ModelOutputLayer | DatasetLayer) => {
                const cache = layer.type === "ModelOutputLayer" ? modelOutputCache : datasetOutputCache
                const id = layer.type === "ModelOutputLayer" ? layer.nodeId : layer.id
                return cache[id] ? cache[id] : null
              }}
              currentTab={currentTab}
              projectExtent={projectExtent}
              // for now, only show extents for the current team as the reference extents require extra logic
              ExtentList={teamExtents}
              extentShape={extentShape}
              setExtentShape={setExtentShape}
              projectExtentShape={projectExtentShape}
              model={state.project.model}
              projectLayers={state.project.layers}
              layerOrder={state.project.allLayers}
              selectedLayerId={state.selectedLayer}
            />
          }
        </>}
          {
            sidebarVisible
              ? <div className={currentTab === Tab.MapView ? undefined : "d-none"} style={publicShare ? { position: "absolute", inset: 0, pointerEvents: "none", zIndex: 4 } : { height: "100%" }}>
              {publicShare ? (
              <PublicSidebar
                state={state}
                selectLayer={id => {
                  setSelectedLocalGeotiffId(null)
                  dispatch({ type: "SelectLayer", id })
                }}
                mutateLayer={(id, data) => dispatch({ type: "MutateLayer", id, data })}
                model={state.project.model}
                mapLayerStack={mapLayerStackReconciled}
                reorderMapLayers={reorderMapLayers}
                localGeotiffLayers={localGeotiffLayers}
                mutateLocalGeotiff={mutateLocalGeotiff}
                removeLocalGeotiff={removeLocalGeotiff}
                selectedLocalGeotiffId={selectedLocalGeotiffId}
                onSelectLocalGeotiff={localId => {
                  setSelectedLocalGeotiffId(cur => (cur === localId ? null : localId))
                  dispatch({ type: 'SelectLayer', id: undefined })
                }}
                showLayerPalette={() => setLayerPaletteVisible(open => !open)}
                layerPaletteOpen={layerPaletteVisible}
                getLayerData={(layer: ModelOutputLayer | DatasetLayer) => {
                  const cache = layer.type === "ModelOutputLayer" ? modelOutputCache : datasetOutputCache
                  const id = layer.type === "ModelOutputLayer" ? layer.nodeId : layer.id
                  return cache[id] ? cache[id].getStats() : { min: 0, max: 0, type: undefined, zoom: 0, area: 0, length: 0 }
                }}
                getTileGrid={(layer: ModelOutputLayer | DatasetLayer) => {
                  const cache = layer.type === "ModelOutputLayer" ? modelOutputCache : datasetOutputCache
                  const id = layer.type === "ModelOutputLayer" ? layer.nodeId : layer.id
                  return cache[id] ?? null
                }}
                setSelectedLayer={setSelectedLayer}
                selectedLayer={selectedLayer}
              />
              ) : (
              <Sidebar
                visible={currentTab === Tab.MapView}
                state={state}
                selectLayer={id => {
                  setSelectedLocalGeotiffId(null)
                  dispatch({ type: "SelectLayer", id })
                }}
                mutateLayer={(id, data) => dispatch({ type: "MutateLayer", id, data })}
                updateModelOutputNodeName={(nodeId, name) => modelEditorApi?.updateNodeName(nodeId, name)}
                model={state.project.model}
                modelEditorApi={modelEditorApi}
                highlightedCategoryGroupKey={highlightedCategoryGroupKey}
                deleteLayer={id => dispatch({ type: "DeleteLayer", id })}
                mapLayerStack={mapLayerStackReconciled}
                reorderMapLayers={reorderMapLayers}
                localGeotiffLayers={localGeotiffLayers}
                mutateLocalGeotiff={mutateLocalGeotiff}
                removeLocalGeotiff={removeLocalGeotiff}
                selectedLocalGeotiffId={selectedLocalGeotiffId}
                onSelectLocalGeotiff={localId => {
                  setSelectedLocalGeotiffId(cur => (cur === localId ? null : localId))
                  dispatch({ type: 'SelectLayer', id: undefined })
                }}
                showLayerPalette={() => setLayerPaletteVisible(true)}
                hide={() => setSidebarVisible(false)}
                getLayerData={(layer: ModelOutputLayer | DatasetLayer) => {
                  const cache = layer.type === "ModelOutputLayer" ? modelOutputCache : datasetOutputCache
                  const id = layer.type === "ModelOutputLayer" ? layer.nodeId : layer.id
                  return cache[id] ? cache[id].getStats() : { min: 0, max: 0, type: undefined, zoom: 0, area: 0, length: 0 }
                }}
                getTileGrid={(layer: ModelOutputLayer | DatasetLayer) => {
                  const cache = layer.type === "ModelOutputLayer" ? modelOutputCache : datasetOutputCache
                  const id = layer.type === "ModelOutputLayer" ? layer.nodeId : layer.id
                  return cache[id] ?? null
                }}
                setSelectedLayer={setSelectedLayer}
                selectedLayer={selectedLayer}
                exportLayerAsGeoTiff={async (layer: ModelOutputLayer | DatasetLayer) => {

                  setExportStatus({
                    status: 'preparing',
                    progress: 0,
                    message: 'Creating export package...'
                  })

                  const cache = layer.type === "ModelOutputLayer" ? modelOutputCache : datasetOutputCache
                  const id = layer.type === "ModelOutputLayer" ? layer.nodeId : layer.id
                  const tileGrid = cache[id]
                  
                  if (!tileGrid) {
                    console.error('No tile grid data available for export')
                    setExportStatus({
                      status: 'error',
                      progress: 0,
                      message: 'No tile grid data available for export',
                      error: 'No tile grid data available'
                    })
                    return
                  }
                  
                  // Reset status and start export process
                  setExportStatus({
                    status: 'preparing',
                    progress: 10,
                    message: 'Preparing data for export...'
                  })
                  
                  const exportColors = exportColorsForLayer(tileGrid, layer)
                  const tileGridJSON = tileGrid.toJSON({ 
                    colors: exportColors, 
                    fill: layer.fill 
                  })
                  setDownloading(layer.name)
                  setExtraInfo(`Exporting ${layer.name} to GeoTIFF... `)
                  
                  try {
                    
                    // Create FormData for multipart request
                    const formData = new FormData()
                    const blob = new Blob([JSON.stringify(tileGridJSON)], { type: 'application/json' })
                    formData.append('dataset', blob, `${layer.name}.json`)
                    
                    // Update status for upload
                    setExportStatus({
                      status: 'uploading',
                      progress: 15,
                      message: 'Uploading data to server...'
                    })
                    
                    const response = await fetch(`${LANDSCAPES_V2_API}/geotiff`, {
                      method: 'POST',
                      body: formData
                    })

                    // Update status for processing
                    setExportStatus({
                      status: 'processing',
                      progress: 67,
                      message: 'Server processing GeoTIFF...'
                    })

                    if (tileGridJSON.type === "CategoricalTileGrid") {
                      // Update status for CLR file generation
                      setExportStatus({
                        status: 'processing',
                        progress: 80,
                        message: 'Generating QGIS color map...'
                      })
                      
                      // Generate QGIS color map file
                      const clrContent = generateQGISColorMap(tileGridJSON, exportColors || [])
                      
                      // Create and download the CLR file
                      const clrBlob = new Blob([clrContent], { type: 'text/plain' })
                      const clrUrl = window.URL.createObjectURL(clrBlob)
                      const clrLink = document.createElement('a')
                      clrLink.href = clrUrl
                      clrLink.download = `${layer.name}.clr`
                      document.body.appendChild(clrLink)
                      clrLink.click()
                      window.URL.revokeObjectURL(clrUrl)
                      document.body.removeChild(clrLink)
                    }
                    
                    if (response.ok) {
                      // Update status for download
                      setExportStatus({
                        status: 'downloading',
                        progress: 90,
                        message: 'Preparing download...'
                      })
                      
                      // Get the GeoTIFF blob
                      const geotiffBlob = await response.blob()
                      
                      // Create download link
                      const url = window.URL.createObjectURL(geotiffBlob)
                      const a = document.createElement('a')
                      a.href = url
                      a.download = `${layer.name}.tif`
                      document.body.appendChild(a)
                      a.click()
                      window.URL.revokeObjectURL(url)
                      document.body.removeChild(a)
                      
                      // Complete the process
                      setExportStatus({
                        status: 'completed',
                        progress: 100,
                        message: 'Export completed successfully!'
                      })
                      
                      setDownloading(null)
                      setExtraInfo(null)
                      
                      // Reset status after a delay
                      setTimeout(() => {
                        setExportStatus({
                          status: 'idle',
                          progress: 0,
                          message: ''
                        })
                      }, 3000)
                    } else {
                      const errorMessage = `Server error: ${response.status} ${response.statusText}`
                      console.error('Failed to export GeoTIFF:', response.statusText)
                      setExportStatus({
                        status: 'error',
                        progress: 0,
                        message: 'Export failed',
                        error: errorMessage
                      })
                      setDownloading(null)
                      setExtraInfo(null)
                    }
                  } catch (error) {
                    const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred'
                    console.error('Error exporting GeoTIFF:', error)
                    setExportStatus({
                      status: 'error',
                      progress: 0,
                      message: 'Export failed',
                      error: errorMessage
                    })
                    setDownloading(null)
                    setExtraInfo(null)
                  }
                }}
                exportLayerAsShapefile={async (layer: ModelOutputLayer | DatasetLayer) => {
                  setExportStatus({
                    status: 'preparing',
                    progress: 0,
                    message: 'Creating export package...'
                  })

                  const cache = layer.type === "ModelOutputLayer" ? modelOutputCache : datasetOutputCache
                  const id = layer.type === "ModelOutputLayer" ? layer.nodeId : layer.id
                  const tileGrid = cache[id]

                  if (!tileGrid) {
                    setExportStatus({
                      status: 'error',
                      progress: 0,
                      message: 'No tile grid data available for export',
                      error: 'No tile grid data available'
                    })
                    return
                  }

                  const exportColors = exportColorsForLayer(tileGrid, layer)
                  const tileGridJSON = tileGrid.toJSON({
                    colors: exportColors,
                    fill: layer.fill
                  })

                  if (tileGridJSON.type !== "CategoricalTileGrid") {
                    setExportStatus({
                      status: 'error',
                      progress: 0,
                      message: 'Shapefile export is only available for categorical layers',
                      error: 'Not a categorical layer'
                    })
                    return
                  }

                  setExportStatus({
                    status: 'preparing',
                    progress: 10,
                    message: 'Preparing GeoTIFF for vectorisation...'
                  })

                  setDownloading(layer.name)
                  setExtraInfo(`Exporting ${layer.name} to Shapefile... `)

                  try {
                    const formData = new FormData()
                    const jsonBlob = new Blob([JSON.stringify(tileGridJSON)], { type: 'application/json' })
                    formData.append('dataset', jsonBlob, `${layer.name}.json`)

                    setExportStatus({
                      status: 'uploading',
                      progress: 20,
                      message: 'Uploading data to server...'
                    })

                    const geotiffResponse = await fetch(`${LANDSCAPES_V2_API}/geotiff`, {
                      method: 'POST',
                      body: formData
                    })

                    setExportStatus({
                      status: 'processing',
                      progress: 50,
                      message: 'Building GeoTIFF...'
                    })

                    if (!geotiffResponse.ok) {
                      const errorMessage = `Server error: ${geotiffResponse.status} ${geotiffResponse.statusText}`
                      console.error('Failed to build GeoTIFF for Shapefile:', geotiffResponse.statusText)
                      setExportStatus({
                        status: 'error',
                        progress: 0,
                        message: 'Export failed',
                        error: errorMessage
                      })
                      setDownloading(null)
                      setExtraInfo(null)
                      return
                    }

                    const geotiffBlob = await geotiffResponse.blob()

                    const shpForm = new FormData()
                    shpForm.append('geotiff', geotiffBlob, `${layer.name}.tif`)

                    const labels = tileGridJSON.labels
                    if (labels && typeof labels === 'object' && Object.keys(labels).length > 0) {
                      const clrContent = generateQGISColorMap(tileGridJSON, exportColors || [])
                      shpForm.append('clr', new Blob([clrContent], { type: 'text/plain' }), `${layer.name}.clr`)
                    }

                    shpForm.append('smooth', String(GEOTIFF_TO_SHP_SMOOTH))
                    shpForm.append('smooth_iters', String(GEOTIFF_TO_SHP_SMOOTH_ITERS))

                    setExportStatus({
                      status: 'processing',
                      progress: 70,
                      message: 'Converting to Shapefile...'
                    })

                    const shpResponse = await fetch(`${LANDSCAPES_V2_API}/geotiff_to_shp`, {
                      method: 'POST',
                      body: shpForm
                    })

                    if (!shpResponse.ok) {
                      const errorMessage = `Server error: ${shpResponse.status} ${shpResponse.statusText}`
                      console.error('Failed to export Shapefile:', shpResponse.statusText)
                      setExportStatus({
                        status: 'error',
                        progress: 0,
                        message: 'Shapefile export failed',
                        error: errorMessage
                      })
                      setDownloading(null)
                      setExtraInfo(null)
                      return
                    }

                    setExportStatus({
                      status: 'downloading',
                      progress: 90,
                      message: 'Preparing download...'
                    })

                    const zipBlob = await shpResponse.blob()
                    const url = window.URL.createObjectURL(zipBlob)
                    const a = document.createElement('a')
                    a.href = url
                    a.download = `${layer.name}_shapefile.zip`
                    document.body.appendChild(a)
                    a.click()
                    window.URL.revokeObjectURL(url)
                    document.body.removeChild(a)

                    setExportStatus({
                      status: 'completed',
                      progress: 100,
                      message: 'Export completed successfully!'
                    })

                    setDownloading(null)
                    setExtraInfo(null)

                    setTimeout(() => {
                      setExportStatus({
                        status: 'idle',
                        progress: 0,
                        message: ''
                      })
                    }, 3000)
                  } catch (error) {
                    const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred'
                    console.error('Error exporting Shapefile:', error)
                    setExportStatus({
                      status: 'error',
                      progress: 0,
                      message: 'Export failed',
                      error: errorMessage
                    })
                    setDownloading(null)
                    setExtraInfo(null)
                  }
                }}
                saveLayerAsDataset={async (layer: ModelOutputLayer | DatasetLayer) => {
                  setExportStatus({
                    status: 'preparing',
                    progress: 0,
                    message: 'Preparing dataset...'
                  })

                  const cache = layer.type === "ModelOutputLayer" ? modelOutputCache : datasetOutputCache
                  const id = layer.type === "ModelOutputLayer" ? layer.nodeId : layer.id
                  const tileGrid = cache[id]
                  
                  if (!tileGrid) {
                    console.error('No tile grid data available for saving')
                    setExportStatus({
                      status: 'error',
                      progress: 0,
                      message: 'No tile grid data available for saving',
                      error: 'No tile grid data available'
                    })
                    return
                  }
                  
                  setExportStatus({
                    status: 'preparing',
                    progress: 10,
                    message: 'Preparing data for save...'
                  })
                  
                  const exportColors = exportColorsForLayer(tileGrid, layer)
                  const tileGridJSON = tileGrid.toJSON({ 
                    colors: exportColors, 
                    fill: layer.fill 
                  })
                  setDownloading(layer.name)
                  setExtraInfo(`Saving ${layer.name} as dataset... `)
                  
                  try {
                    setExportStatus({
                      status: 'uploading',
                      progress: 50,
                      message: 'Saving dataset to server...'
                    })
                    
                    saveModelOutput(layer.name, tileGridJSON, teamId, (status) => {
                      if (status === 201 || status === 200) {
                        setExportStatus({
                          status: 'completed',
                          progress: 100,
                          message: 'Dataset saved successfully!'
                        })
                        
                        setDownloading(null)
                        setExtraInfo(null)
                        
                        // Reset status after a delay
                        setTimeout(() => {
                          setExportStatus({
                            status: 'idle',
                            progress: 0,
                            message: ''
                          })
                        }, 3000)
                      } else {
                        const errorMessage = `Server error: ${status}`
                        console.error('Failed to save dataset:', status)
                        setExportStatus({
                          status: 'error',
                          progress: 0,
                          message: 'Save failed',
                          error: errorMessage
                        })
                        setDownloading(null)
                        setExtraInfo(null)
                      }
                    })
                  } catch (error) {
                    const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred'
                    console.error('Error saving dataset:', error)
                    setExportStatus({
                      status: 'error',
                      progress: 0,
                      message: 'Save failed',
                      error: errorMessage
                    })
                    setDownloading(null)
                    setExtraInfo(null)
                  }
                }}
                downloading={downloading}
                exportStatus={exportStatus}
              />
              )}
              </div>
              : currentTab === Tab.MapView
                ? <CollapsedSidebar show={() => setSidebarVisible(true)} />
                : null
          }
        <ModelView
          visible={currentTab === Tab.ModelView}
          onFocusMapOutput={focusMapOutput}
          initialTransform={modelViewTransform}
          setTransform={setModelViewTransform}
          initialModel={state.project.model}
          setModel={(model: Data) => dispatch({ type: "SetModel", model })}
          projectId={projectId}
          createOutputLayer={(nodeId: number) => {
            const nextId = allocateProjectLayerId()
            dispatch({
              type: "AddLayer",
              layer: {
                name: "Untitled model output",
                visible: true,
                opacity: 1,
                type: "ModelOutputLayer",
                nodeId,
                fill: "greyscale",
                colors: [],
                overrideBounds: false,
                bounds: undefined
              }
            })
            if (publicShare) noteAddedPublicLayer(nextId)
          }}
          deleteOutputLayer={(nodeId: number) =>
            dispatch({ type: "DeleteModelOutputLayer", nodeId })
          }
          getOutputLayerStyle={(nodeId: number) => {
            const layer = Object.values(projectLayersRef.current).find(
              l => l.type === "ModelOutputLayer" && l.nodeId === nodeId
            )
            if (!layer || layer.type !== "ModelOutputLayer") return null
            return {
              fill: layer.fill,
              colors: layer.colors,
              colorByLabel: layer.colorByLabel,
            }
          }}
          mutateOutputLayer={(nodeId, data) => {
            // Resolve by nodeId inside the reducer so add-then-style in one
            // agent turn still finds the layer (sequential useReducer updates
            // compose). Do not pass nodeId as `id` — layer keys and rete node
            // ids collide, which would mutate the wrong sidebar layer.
            dispatch({ type: "MutateLayer", nodeId, data })
            return true
          }}
          listMapLayerDescriptions={() => {
            const notes: Array<{ nodeId: number, name: string, comment: string }> = []
            Object.values(projectLayersRef.current).forEach(layer => {
              if (layer.type !== "ModelOutputLayer") return
              const comment = layer.comment?.trim()
              if (!comment) return
              notes.push({ nodeId: layer.nodeId, name: layer.name, comment })
            })
            return notes
          }}
          onEditorReady={setModelEditorApi}
          onAgentActionsReady={onAgentActionsReady}
          publicExtentApiRef={publicExtentApiRef}
          onInitialLoadComplete={() => setModelLoaded(true)}
          onAvailableComponents={setAvailableComponents}
          saveMapLayer={(id, name, tileGrid) => {
            const builtFor = gridGeographyKey(tileGrid)
            if (builtFor && builtFor !== publishedGeographyRef.current) return
            setModelOutputCache(prev => ({ ...prev, [id]: tileGrid }))
            if (name) dispatch({ type: "MutateLayer", nodeId: id, data: { name } })
          }}
          setProcessing={setProcessing}
          autoProcessing={state.autoProcessing}
          process={process}
          setProcess={setProcess}
          saveModel={(name: string, json: TileGridJSON, callback?: (status: number) => void) => saveModelOutput(name, json, teamId, callback)}
          getDatasets={() => getDatasets(teamId)}
          getAnnotations={() => getAnnotations(teamId)}
          extent={projectExtent}
          zoom={projectZoom}
          mask={projectMask}
          maskLayer={projectMaskSource}
          maskCQL={projectMaskCQL}
          maskShape={geographySource.shape}
          permissions={permissions}
          getCustomExpressions={() => getExpressions(teamId)}
          debug={debug}
          extentList={teamExtents.map((extent) => ({ name: extent.name, id: extent.id, level: extent.level ?? null }))}
          externalLink={geographySource.externalLink}
          setExtraInfo={setExtraInfo}
          teamName={teamName}
          teamId={teamId}
          teamComponents={teamComponents}
          groqConfigured={groqConfigured}
          agentConfigured={agentConfigured}
          agentProvider={agentProvider}
          agentModel={agentModel}
          agentProviderId={agentProviderId}
          agentModelId={agentModelId}
        />
        </AegisSessionProvider>
      </div>
    </div>
  )
}
